import { defineStore } from 'pinia'
import { reactive, ref, toRaw } from 'vue'
import * as oddsService from '@/api/oddsService'
import { getInferredCurrency } from '@/utils/conductor.util'
import type { Region, UserProfile } from '@/types/user'

export const useProfilesStore = defineStore('preferences', () => {
  const localKey = 'userPreferences'

  const profile = reactive<UserProfile>({
    userId: 0,
    username: '',
    regions: [],
    displayCount: 0,
    betFormat: 'decimal',
    preferredCurrency: '',
    included: [],
    excluded: [],
    hiddenTemplates: []
  })

  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const isSystemUpdate = ref(false)

  // ------------------ Local Storage ------------------
  function loadLocal() {
    const data = localStorage.getItem(localKey)
    if (data) {
      try {
        const parsed = JSON.parse(data)
        if (!parsed.hiddenTemplates) parsed.hiddenTemplates = []
        Object.assign(profile, parsed)
      } catch (err) {
        console.error('Failed to parse local profile:', err)
        localStorage.removeItem(localKey)
      }
    }

    if (!profile.preferredCurrency) {
      profile.preferredCurrency = getInferredCurrency()
      saveLocal()
    }
  }

  function saveLocal(rawProfile?: Partial<UserProfile>) {
    try {
      const toSave = rawProfile ? rawProfile : toRaw(profile)
      localStorage.setItem(localKey, JSON.stringify(toSave))
    } catch (err) {
      console.error('Failed to save profile to localStorage:', err)
    }
  }

  // ------------------ DB ------------------
  async function saveToDB(userId: number) {
    isLoading.value = true
    error.value = null
    try {
      const profileData = toRaw(profile)
      await oddsService.updateUserProfile({ ...profileData, userId })
    } catch (err: any) {
      error.value = err.message || 'Failed to save preferences'
    } finally {
      isLoading.value = false
    }
  }

  async function loadFromDB(checkLoginFn?: () => Promise<void>) {
    isLoading.value = true
    error.value = null
    isSystemUpdate.value = true
    try {
      if (checkLoginFn) await checkLoginFn()
      saveLocal()
    } catch (err: any) {
      error.value = err.message || 'Failed to load preferences'
    } finally {
      isSystemUpdate.value = false
      isLoading.value = false
    }
  }

  async function getBookieRegions(): Promise<Region[]> {
    return await oddsService.getBookieRegions()
  }

  // ------------------ System Updates ------------------
  function setUserProfile(profileFromServer: UserProfile) {
    isSystemUpdate.value = true
    try {
      const mergedProfile = { ...profileFromServer }
      if (!mergedProfile.hiddenTemplates) mergedProfile.hiddenTemplates = []
      
      const localCurrency = profile.preferredCurrency
      const serverCurrency = mergedProfile.preferredCurrency
      
      if (!serverCurrency || serverCurrency === '') {
        if (localCurrency && localCurrency !== '') {
          mergedProfile.preferredCurrency = localCurrency
        } else {
          mergedProfile.preferredCurrency = getInferredCurrency()
        }

        // Sync to DB
        if (mergedProfile.userId) {
          setTimeout(() => saveToDB(mergedProfile.userId), 500)
        }
      }
      
      Object.assign(profile, mergedProfile)
      saveLocal(mergedProfile)
    } finally {
      isSystemUpdate.value = false
    }
  }

  function resetProfile() {
    isSystemUpdate.value = true
    try {
      profile.userId = 0
      profile.username = ''
      profile.regions = []
      profile.displayCount = 0
      profile.betFormat = 'decimal'
      profile.preferredCurrency = ''
      profile.included = []
      profile.excluded = []
      profile.hiddenTemplates = []
    } finally {
      isSystemUpdate.value = false
    }
  }

  return {
    profile,
    isLoading,
    error,
    isSystemUpdate,
    loadLocal,
    saveLocal,
    saveToDB,
    loadFromDB,
    setUserProfile,
    resetProfile,
    getBookieRegions,
  }
})